import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import {useState} from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import Button from "@khanacademy/wonder-blocks-button";
import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import type {Result} from "@khanacademy/wonder-blocks-data";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useMutation} from "@khan/data-gql";
import {gqlOp} from "@khan/data-gql-core";
import {mediaQueries} from "@khan/shared-styles";
import {preserveMarketingParams, staticUrl, useLocation} from "@khan/urls";
import {RequireReauth} from "@khan/user-settings";

import InnerBirthdayPicker from "#components/birthday-picker/birthday-picker.tsx";
import {isDateOver18} from "#shared-package/is-over-18.ts";

import type {
    saveFreeForTeachersEligibilityForm,
    saveFreeForTeachersEligibilityFormVariables,
} from "./__graphql-types__/saveFreeForTeachersEligibilityForm.ts";
import useFreeForTeachersTerms from "./use-free-for-teachers-terms.ts";

interface Props {
    birthdate?: string;
    afterEligibleSave?: (birthdate: string, agreesToTerms: boolean) => void;
    // Username or email of the account to be logged into if re-auth
    // is triggered.
    reauthAccountIdentifier: string;
}

type MessageKey = "successfully_reauthenticated" | "clever_reauth_unsuccessful";
type BannerMessage = {
    kind: "success" | "critical";
    message: string;
};

export default function FreeForTeachersEligibilityForm(props: Props) {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const {search, queryParams} = useLocation();
    const termsURL = useFreeForTeachersTerms();

    const [bannerDismissed, setBannerDismissed] = useState(false);

    const handleBannerDismiss = () => {
        setBannerDismissed(true);
    };

    useOnMountEffect(() => {
        publishAnalyticsEvent({
            eventType: "AiGuideTeacherFeaturesEnrollFormPageViewed",
            eventSchemaVersion: 1,
        });

        publishAnalyticsEvent({
            eventType: "AiGuideTeacherToolsFlowStartedTI",
            eventSchemaVersion: 1,
        });
    });

    const initialBirthdate = props.birthdate || "";

    const [birthdate, setBirthdate] = useState<string>(initialBirthdate);
    const [agreesToTerms, setAgreesToTerms] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const [saveEligibility, saveEligibilityResult] = useMutation(
        FREE_FOR_TEACHERS_SAVE_ELIGIBILITY_MUTATION,
    );

    const knownUnderage = birthdate && !isDateOver18(birthdate);

    const meetsCriteria: boolean = isDateOver18(birthdate) && agreesToTerms;

    const isLoading: boolean =
        !!saveEligibilityResult && saveEligibilityResult.status === "loading";

    const saveErrors = saveEligibilityResult
        ? getGraphQLErrors(saveEligibilityResult)
        : [];

    const saveDidFail: boolean =
        saveEligibilityResult?.status === "error" || saveErrors.length > 0;

    const changeBirthdate = (newDate?: string | null) => {
        setBirthdate(newDate || "");
    };

    const changeTerms = (newTerms?: boolean | null) => {
        setAgreesToTerms(!!newTerms);
    };

    const submit = (evt: React.FormEvent<HTMLFormElement>) => {
        evt.preventDefault();
        setSubmitAttempted(true);
    };

    const save = () => {
        if (!meetsCriteria) {
            return;
        }

        saveEligibility({
            teacherRoleOp: "ADD",
            birthdate,
        }).then(async (result) => {
            if (result.status !== "success") {
                return;
            }

            const errors = getGraphQLErrors(result);

            if (errors.length) {
                return;
            }

            if (props.afterEligibleSave) {
                props.afterEligibleSave(birthdate, agreesToTerms);
            }

            await publishAnalyticsEvent({
                eventType: "AiGuideTeacherToolsFlowCompletedTI",
                eventSchemaVersion: 1,
            });
        });
    };

    const renderSubmitButton = (onClick: () => unknown) => {
        return (
            <Button
                type="submit"
                onClick={onClick}
                disabled={!meetsCriteria}
                spinner={isLoading}
                size="large"
                style={styles.formButton}
            >
                {t`Let's go!`}
            </Button>
        );
    };

    // If the user is presented with a request to re-authenticate
    // and they choose to authenticate with a single-sign-on provider
    // who navigates the browser to a page on their own domain where
    // should the user be returned to when the provider has completed
    // authentication?
    const reauthContinueUrl = preserveMarketingParams(
        "/khanmigo/teacher-tools",
        new URLSearchParams(search),
    );

    const {messageKey} = queryParams;
    const MESSAGE_KEY_MAPPING: Record<MessageKey, BannerMessage> = {
        successfully_reauthenticated: {
            kind: "success",
            message: t`Successfully verified your account. You may now change your settings.`,
        },
        clever_reauth_unsuccessful: {
            kind: "critical",
            message: t`Sorry, reauthentication through Clever has failed.`,
        },
    };
    const bannerConfig: BannerMessage | undefined =
        MESSAGE_KEY_MAPPING[messageKey];

    return (
        <div className={css(styles.frame)}>
            <div className={css(styles.promoFrame)}>
                <View style={styles.promo}>
                    <div className={css(styles.promoIcon)}>
                        <img
                            src={staticUrl(
                                "/images/khanmigo/holding-back-tears-khanmigo-2x.png",
                            )}
                            alt=""
                            width="130"
                            className={css(styles.promoIcon)}
                        />
                        <Strut size={spacing.medium_16} />
                    </div>
                    <p className={css(styles.promoHeadline)}>
                        {t`Almost done…!`}
                    </p>
                    <Strut size={spacing.medium_16} />
                    <p className={css(styles.promoBody)}>
                        {t`We need a few more details for you to get free access to Khanmigo, your go-to AI tool for teaching.`}
                    </p>
                </View>
            </div>
            <div className={css(styles.formFrame)}>
                <form onSubmit={submit} className={css(styles.form)}>
                    <View>
                        {bannerConfig && !bannerDismissed && (
                            <>
                                <Banner
                                    text={bannerConfig.message}
                                    layout="floating"
                                    kind={bannerConfig.kind}
                                    onDismiss={handleBannerDismiss}
                                />
                                <Strut size={spacing.medium_16} />
                            </>
                        )}
                        <div role="alert">
                            {saveDidFail && (
                                <View>
                                    <LabelMedium style={styles.formErrorText}>
                                        {t`An unknown error prevented your details from saving.`}
                                    </LabelMedium>
                                    <Strut size={spacing.medium_16} />
                                </View>
                            )}
                            {knownUnderage && (
                                <View>
                                    <LabelMedium style={styles.formErrorText}>
                                        {t`You are not eligible for this account type.`}
                                    </LabelMedium>
                                    <Strut size={spacing.medium_16} />
                                </View>
                            )}
                        </div>
                        <LabelMedium>{t`What is your date of birth?`}</LabelMedium>
                        <Strut size={spacing.medium_16} />
                        <InnerBirthdayPicker
                            defaultValue={birthdate}
                            onChange={changeBirthdate}
                        />
                        <div role="alert">
                            {submitAttempted && !birthdate && (
                                <View>
                                    <Strut size={spacing.xSmall_8} />
                                    <LabelSmall style={styles.formErrorText}>
                                        {t`Please enter your birthdate.`}
                                    </LabelSmall>
                                </View>
                            )}
                        </div>
                        <Strut size={spacing.large_24} />
                        <Checkbox
                            label={
                                <Trans>
                                    By checking this box, you agree to the{" "}
                                    <Link href={termsURL} target="_blank">
                                        Khanmigo Teacher Tools supplement
                                    </Link>
                                    . You confirm that you qualify for this type
                                    of account access as a teacher.
                                </Trans>
                            }
                            checked={agreesToTerms}
                            onChange={changeTerms}
                            testId="agrees-to-terms"
                        />
                        <div role="alert">
                            {submitAttempted && !agreesToTerms && (
                                <View>
                                    <Strut size={spacing.xSmall_8} />
                                    <LabelSmall style={styles.formErrorText}>
                                        {t`Terms and conditions must be accepted to qualify for this offer.`}
                                    </LabelSmall>
                                </View>
                            )}
                        </div>
                        <Strut size={spacing.medium_16} />
                        {birthdate !== initialBirthdate && (
                            <RequireReauth
                                identifier={props.reauthAccountIdentifier}
                                onSuccess={save}
                                onFailure={() => {}}
                                continueUrl={reauthContinueUrl}
                            >
                                {(invokeReauth) =>
                                    renderSubmitButton(invokeReauth)
                                }
                            </RequireReauth>
                        )}
                        {birthdate === initialBirthdate &&
                            renderSubmitButton(save)}
                    </View>
                </form>
            </div>
        </div>
    );
}

function getGraphQLErrors(result: Result<saveFreeForTeachersEligibilityForm>) {
    const errors: Array<string> = [];

    if (result.status !== "success") {
        return errors;
    }

    if (result.data.setSettings?.errors) {
        result.data.setSettings.errors.forEach((gqlError) => {
            if (gqlError) {
                errors.push(gqlError.code);
            }
        });
    }

    if (result.data.updateUserRole?.error) {
        errors.push(result.data.updateUserRole.error.code);
    }

    return errors;
}

export const FREE_FOR_TEACHERS_SAVE_ELIGIBILITY_MUTATION = gqlOp<
    saveFreeForTeachersEligibilityForm,
    saveFreeForTeachersEligibilityFormVariables
>(gql`
    mutation saveFreeForTeachersEligibilityForm(
        $teacherRoleOp: RoleOperation!
        $birthdate: String!
    ) {
        setSettings(birthdate: $birthdate) {
            errors {
                code
            }
        }
        updateUserRole(role: TEACHER, operation: $teacherRoleOp) {
            error {
                code
            }
        }
    }
`);

const styles = StyleSheet.create({
    frame: {
        width: "100%",
        [mediaQueries.lgOrLarger]: {
            display: "flex",
            height: "calc(100vh - 74px)",
        },
    },
    promoFrame: {
        background: "#f7f2ea",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "5%",
        [mediaQueries.lgOrLarger]: {
            width: "50%",
        },
    },
    formFrame: {
        flexGrow: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: color.white,
        padding: "5%",
        [mediaQueries.lgOrLarger]: {
            width: "50%",
        },
    },
    promo: {
        alignItems: "left",
        textAlign: "left",
        maxWidth: "470px",
        WebkitFontSmoothing: "antialiased",
        [mediaQueries.smOrLarger]: {
            alignItems: "center",
            textAlign: "center",
        },
    },
    promoIcon: {
        display: "none",
        [mediaQueries.lgOrLarger]: {
            display: "block",
        },
    },
    promoHeadline: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: "28px",
        lineHeight: "32px",
        margin: 0,
        [mediaQueries.lgOrLarger]: {
            fontSize: "44px",
            lineHeight: "48px",
        },
    },
    promoBody: {
        fontFamily: "Lato, 'Noto Sans', Helvetica, Corbel, sans-serif",
        fontSize: "14px",
        lineHeight: "18px",
        margin: 0,
        [mediaQueries.lgOrLarger]: {
            fontSize: "20px",
            lineHeight: "32px",
        },
    },
    form: {
        maxWidth: "400px",
    },
    formErrorText: {
        color: color.red,
        fontStyle: "italic",
    },
    formButton: {
        alignSelf: "flex-start",
    },
});
